import { Trans, useLingui } from "@lingui/react/macro";
import React from "react";
import * as styles from "./ShipDeckPlanSection.module.scss";
import {
  ResponsiveSanityImageProps,
  SectionContainer,
  SanityImageRatio,
  ResponsiveSanityImage,
  ButtonsContainer,
  Button
} from "@silversea-ssc/ui-components-react";

interface ShipDeckPlanSectionProps {
  shipName: string;
  deckPlanUrl: string;
  lateralViewImage: ResponsiveSanityImageProps["image"];

  crewCount?: number;
  passengerCount?: number;
  buildYear?: number;
  lastRefurbishmentYear?: number;
  tonnage?: number;
}

const ShipDeckPlanSection: React.FC<
  React.PropsWithChildren<ShipDeckPlanSectionProps>
> = ({
  children,
  shipName,
  deckPlanUrl,
  lateralViewImage,
  crewCount,
  passengerCount,
  buildYear,
  lastRefurbishmentYear,
  tonnage
}) => {
  const { i18n } = useLingui();
  return (
    <SectionContainer
      backgroundColor="transparent"
      dataContentName="ship_deck_plan_section"
    >
      <div className={styles.container}>
        <div className={styles.title}>
          {shipName}
          <br /> <Trans>Deck plan</Trans>
        </div>
        <SanityImageRatio
          ratio="auto"
          image={lateralViewImage}
          className={styles.image}
        >
          <ResponsiveSanityImage
            image={lateralViewImage}
            defaultWidth={427}
            defaultHeight={103}
            alt={shipName}
            fit="original"
            loading="lazy"
          />
        </SanityImageRatio>

        <div className={styles.description}>{children}</div>
        <ul className={styles.techData}>
          {passengerCount && (
            <li className={styles.techDataItem}>
              <Trans>Guests capacity</Trans>: <strong>{passengerCount}</strong>
            </li>
          )}
          {crewCount && (
            <li className={styles.techDataItem}>
              <Trans>Crew capacity</Trans>: <strong>{crewCount}</strong>
            </li>
          )}
          {buildYear && (
            <li className={styles.techDataItem}>
              <Trans>Built</Trans>: <strong>{buildYear}</strong>
            </li>
          )}
          {lastRefurbishmentYear && (
            <li className={styles.techDataItem}>
              <Trans>Last refurbishment</Trans>:{" "}
              <strong>{lastRefurbishmentYear}</strong>
            </li>
          )}
          {tonnage && (
            <li className={styles.techDataItem}>
              <Trans>Tonnage</Trans>:{" "}
              <strong>
                {i18n.number(tonnage)} <Trans>Tons</Trans>
              </strong>
            </li>
          )}
        </ul>
        <div className={styles.cta}>
          <ButtonsContainer mobileFullSize>
            <Button
              href={deckPlanUrl}
              variant="primary"
              iconName="open-tab"
              target="_blank"
              dataContentPiece="download_deck_plan"
              dataContentParams={{ shipName: shipName }}
              rel="nofollow noopener"
              noWrap
            >
              <Trans>View Deck Plan</Trans>
            </Button>
          </ButtonsContainer>
        </div>
      </div>
    </SectionContainer>
  );
};

export default ShipDeckPlanSection;
